import React, { useMemo, useState } from "react";
import { format } from "date-fns";

import { API, Storage, graphqlOperation } from "aws-amplify";
import { sanitizeInboundLambda } from "../../../graphql/inbound/inboundCRUD";
import { sanitizeInventoryLambda } from "../../../graphql/inventory/inventoryCRUD";

import { v4 as uuidv4 } from "uuid";

import {
  Backdrop,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  TextField,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { Button, TopToolbar, useNotify, useRefresh } from "react-admin";
import {
  DocumentIcon,
  ReturnIcon,
} from "../../../components/common/iconography";
import { BackButton } from "../../../components/common/BackButton";
import {
  getCustomFieldIdCleansed,
  parseCustomField,
} from "../../../components/common/CustomFields";
import { HelpButton } from "../../../components/common/HelpButton";
import { SendPOSDialogButton } from "../../../components/common/SendPOSDialogButton";
import UnlinkButton from "../../../components/common/shipWith/UnlinkButton";
import { getEnv, isAuthorized, logError } from "../../../utils/appUtils";
import {
  AclType,
  CarrierType,
  FilePaths,
  InboundState,
  InboundStatus,
  LotType,
  NoteType,
  OutboundStatus,
  ReturnReason,
  ReturnReasonChoices,
  Unit,
  InventoryStatus,
  SORTKEY_BUFFER,
} from "../../../utils/constants";
import {
  downloadFile,
  fetchTemplatePath,
  getTemplatePath,
} from "../../../utils/fileUtils";
import {
  calculateMbf,
  calculateMsf,
  convertUnits,
  getPPU,
  getUnitName,
  isLumberCommodity,
  isPanelsCommodity,
} from "../../../utils/loadTypeUtils";
import {
  deepRedirect,
  extractCounter,
  getBarcodesString,
  normalizeValue,
  trimDecimal,
} from "../../../utils/utils";

import { useOperatingAccount } from "../accounts/AccountUtils";
import { OutboundEditHelp } from "./OutboundHelp";
import { CreateInvoiceButton } from "./CreateInvoiceButton";
import {
  updateShipmentLineItem,
  updateShipmentLineItemInventoryItem,
} from "../../../graphql/mutations";
import {
  calcTotalQuantity,
  getPOSEmailSubject,
  handleSendPOS,
  incrementBOLCounter,
  updateLambdaOutboundMini,
} from "./OutboundUtils";
import { fetchLatestGateCheckIn } from "../gateCheckIns/gateCheckInUtils";

// TEMPORARY — ATS only (American Transloading Solutions). Their BOL template
// prints a "Driver's Name" custom field; until the driver name has a proper
// home on the document, getBOLData fills that field from the shipment's gate
// check-in row. Remove once the permanent field lands.
const ATS_RELOAD_YARD_ID = "18567be7-5e38-4a33-b947-f380620326c1";
const fetchAtsGateCheckIn = async (shipment) => {
  if (shipment?.reloadYardId !== ATS_RELOAD_YARD_ID) return null;
  try {
    return await fetchLatestGateCheckIn("out", shipment.id);
  } catch (err) {
    // The driver name is a nice-to-have; never block the BOL on it.
    logError(
      "outboundShipment:OutboundEditActions:fetchAtsGateCheckIn",
      err,
      false,
    );
    return null;
  }
};

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

export const OutboundEditActions = ({
  data,
  loading,
  backPath,
  setBolFile,
  setBolNumber,
  ...props
}) => {
  const classes = useStyles();
  const notify = useNotify();
  const refresh = useRefresh();
  const isMedium = useMediaQuery((theme) => theme.breakpoints.up("sm"));
  const [isSaving, setIsSaving] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnReason, setReturnReason] = useState(ReturnReason.OVERWEIGHT);
  const [returnItems, setReturnItems] = useState([]);
  const [operatingAccount] = useOperatingAccount(data);

  const getProductData = (product, shippedQty, totalUOM) => {
    if (product) {
      const bolProduct = {
        name: product.name,
        productCode: product.productCode,
        category: product.category,
        defaultUOM: product.defaultUOM,
        defaultWeightUOM: product.defaultWeightUOM,
        width: product.width,
        length: product.length,
        height: product.height,
      };
      if (shippedQty && totalUOM) {
        Object.keys(Unit).forEach((key) => {
          if (key !== totalUOM) {
            const unitQty = convertUnits(shippedQty, totalUOM, key, product);
            if (unitQty?.value) bolProduct[key] = unitQty.value;
          } else {
            bolProduct[key] = shippedQty;
          }
        });
      }

      return bolProduct;
    } else return undefined;
  };

  const getBOLData = (data, bolNumber, gateCheckIn = null) => {
    debugger;
    const bolData = { ...data };
    const shipDate = bolData.departureDateTime
      ? new Date(bolData.departureDateTime).toLocaleDateString()
      : bolData.endDateTime
        ? new Date(bolData.endDateTime).toLocaleDateString()
        : bolData.arrivalDateTime
          ? new Date(bolData.arrivalDateTime).toLocaleDateString()
          : new Date(bolData.createDateTime).toLocaleDateString();
    const grossWeight =
      !isNaN(bolData.ladenWeight) && !isNaN(bolData.tareWeight)
        ? Number(bolData.ladenWeight) - Number(bolData.tareWeight)
        : "";

    const docData = {
      accountName: bolData.accountName,
      account: {
        name: bolData.accountName,
        street: operatingAccount?.address?.street,
        city: operatingAccount?.address?.city,
        state: operatingAccount?.address?.state,
        zipCode: operatingAccount?.address?.zipCode,
        phone: operatingAccount?.phone,
        email: operatingAccount?.email,
      },
      advisedDate: bolData.createDateTime
        ? format(new Date(bolData.createDateTime), "MM/dd/yyyy")
        : undefined,
      arrivalDate: bolData.arrivalDateTime
        ? format(new Date(bolData.arrivalDateTime), "MM/dd/yyyy")
        : undefined,
      arrivalTime: bolData.arrivalDateTime
        ? format(new Date(bolData.arrivalDateTime), "hh:mm a")
        : undefined,
      bolNumber: bolNumber,
      carrierName: bolData.carrierName,
      carrier: bolData.carrierName,
      endDate: bolData.endDateTime
        ? format(new Date(bolData.endDateTime), "MM/dd/yyyy")
        : undefined,
      endTime: bolData.endDateTime
        ? format(new Date(bolData.endDateTime), "hh:mm a")
        : undefined,
      grossWeight: grossWeight,
      ladenWeight: bolData.ladenWeight,
      mustTarp: bolData.mustTarp,
      netWeight: bolData.netWeight,
      pickupNumber: bolData.pickupNumber,
      poNumber: bolData.poNumber,
      consigneePoNumber: bolData.consigneePoNumber,
      railCarNumber: data.railCarNumber ? data.railCarNumber : null,
      reloadYard: {
        name: window.appUser.reloadYard.name,
        street: window.appUser.reloadYard.street,
        city: window.appUser.reloadYard.city,
        state: window.appUser.reloadYard.state,
        zipCode: window.appUser.reloadYard.zipCode,
        phone: window.appUser.reloadYard.phone,
        email: window.appUser.reloadYard.email,
        website: window.appUser.reloadYard.website,
      },
      releaseDateTime: bolData.departureDateTime
        ? format(new Date(bolData.departureDateTime), "MM/dd/yyyy hh:mm a")
        : undefined,
      releaseDate: bolData.departureDateTime
        ? format(new Date(bolData.departureDateTime), "MM/dd/yyyy")
        : undefined,
      releaseTime: bolData.departureDateTime
        ? format(new Date(bolData.departureDateTime), "hh:mm a")
        : undefined,
      scaleWeightUOM: bolData.scaleWeightUnits,
      shipDate: shipDate,
      shipTo: bolData.shipTo,
      startDate: bolData.startDateTime
        ? format(new Date(bolData.startDateTime), "MM/dd/yyyy")
        : undefined,
      startTime: bolData.startDateTime
        ? format(new Date(bolData.startDateTime), "hh:mm a")
        : undefined,
      tareWeight: bolData.tareWeight,
      totalQuantity: calcTotalQuantity(bolData),
      units: bolData.totalUOM,
      uom: bolData.totalUOM,
      uomName: getUnitName(bolData.totalUOM),
      weightUnits: bolData.weightUnits,
      weightUom: bolData.weightUnits,
      weightUomName: getUnitName(bolData.weightUnits),
      instructions: bolData.instructions,
    };

    if (bolData.seals) {
      docData.sealNumbers = getBarcodesString(bolData.seals?.items);
    }

    // add outbound shipment custom fields
    if (bolData.customFields?.length > 0) {
      const customHeaderMap = new Map();
      bolData.customFields.forEach((field) => {
        const fieldValues = parseCustomField(field);

        if (customHeaderMap.has(fieldValues.name)) {
          docData.customFields[customHeaderMap.get(fieldValues.name)] =
            fieldValues.value;
        } else {
          const columnId = getCustomFieldIdCleansed(fieldValues.name);
          customHeaderMap.set(fieldValues.name, columnId);
          docData.customFields = {
            ...docData.customFields,
            [columnId]: fieldValues.value,
          };
        }
      });
    }

    // TEMPORARY — ATS only: the gate check-in's driver name overrides the
    // "Driver's Name" custom field (cleansed id "driversName"). See
    // fetchAtsGateCheckIn above.
    if (
      bolData.reloadYardId === ATS_RELOAD_YARD_ID &&
      gateCheckIn?.driverName
    ) {
      docData.customFields = {
        ...docData.customFields,
        driversName: gateCheckIn.driverName,
      };
    }

    if (bolData.shipmentLineItems?.items?.length > 0) {
      docData.lineItems = [];
      docData.pickList = [];
      let totalNetQty = 0;

      let allMissingPCS = true;
      let somePCSNull = false;
      let totalPCS = 0;

      let totalMBF = 0;
      let allMissingMBF = true;
      let someMBFNull = false;

      let totalMSF = 0;
      let allMissingMSF = true;
      let someMSFNull = false;

      // add shipmentLineItems, product, and inventory custom fields to each line item
      bolData.shipmentLineItems.items.forEach((lineItem, index) => {
        const mergedPcs = getPPU(lineItem.inventory);

        const shippedQty = Number(lineItem?.totalQuantityToPick || 0);
        const returnedQtyFromLine = Number(
          lineItem?.totalQuantityReturned || 0,
        );
        const returnedQtyFromItems = (
          lineItem?.inventoryItems?.items || []
        ).reduce((sum, itm) => sum + Number(itm?.quantityReturned || 0), 0);
        const returnedQty = returnedQtyFromLine || returnedQtyFromItems || 0;
        const netQty = Math.max(0, shippedQty - returnedQty);
        totalNetQty = trimDecimal(totalNetQty + netQty, 3);

        docData.lineItems[index] = {
          productCode: lineItem.inventory?.productCode,
          productName: lineItem.inventory?.productName,
          lotNumber: lineItem.lotNumber,
          // PO of the inventory record (from the inbound), not the outbound's poNumber
          poNumber: lineItem.inventory?.poNumber,
          railCarNumber: lineItem.inventory?.railCarNumber,
          quantity: netQty,
          units: lineItem.inventory?.inventoryUOM
            ? lineItem.inventory.inventoryUOM
            : bolData.totalUOM,
          uom: lineItem.inventory?.inventoryUOM
            ? lineItem.inventory.inventoryUOM
            : bolData.totalUOM,
          uomName: getUnitName(
            lineItem.inventory?.inventoryUOM
              ? lineItem.inventory.inventoryUOM
              : bolData.totalUOM,
          ),
          weight: lineItem.inventory?.weight,
          weightUnits: lineItem.inventory?.weightUOM,
          weightUom: lineItem.inventory?.weightUOM,
          weightUomName: getUnitName(lineItem.inventory?.weightUOM),
        };
        if (lineItem.inventory?.product) {
          docData.lineItems[index].product = getProductData(
            lineItem.inventory?.product,
            netQty,
            lineItem.inventory?.inventoryUOM,
          );
        }

        const ppu = mergedPcs;
        docData.lineItems[index].ppu = ppu;

        // PCS Calculation
        if (ppu == null || ppu === "") {
          somePCSNull = true;
        } else {
          allMissingPCS = false;

          if (bolData.totalUOM === Unit.UNIT) {
            const pcs = trimDecimal(ppu * netQty);
            docData.lineItems[index].pcs = pcs;
            totalPCS = trimDecimal(totalPCS + pcs);
          } else {
            docData.lineItems[index].pcs = ppu;
            totalPCS = trimDecimal(totalPCS + ppu);
          }
        }

        const loadType =
          lineItem.inventory?.reloadYardCommodityId ||
          bolData.reloadYardCommodityId;
        if (isLumberCommodity(loadType)) {
          const mbf = calculateMbf(lineItem.inventory);
          if (mbf == null) {
            someMBFNull = true;
          } else {
            allMissingMBF = false;
            docData.lineItems[index].mbf = `${mbf} MBF`;
            totalMBF = trimDecimal(totalMBF + mbf);
          }
        } else if (isPanelsCommodity(loadType)) {
          const msf = calculateMsf(lineItem.inventory);
          if (msf == null) {
            someMSFNull = true;
          } else {
            allMissingMSF = false;
            docData.lineItems[index].msf = `${msf} MSF`;
            totalMSF = trimDecimal(totalMSF + msf, 3);
          }
        }

        if (lineItem.inventory?.customFields?.length > 0) {
          const customHeaderMap = new Map();
          lineItem.inventory.customFields.forEach((field) => {
            const fieldValues = parseCustomField(field);

            if (customHeaderMap.has(fieldValues.name)) {
              docData.lineItems[index].customFields[
                customHeaderMap.get(fieldValues.name)
              ] = fieldValues.value;
            } else {
              const columnId = getCustomFieldIdCleansed(fieldValues.name);
              customHeaderMap.set(fieldValues.name, columnId);
              docData.lineItems[index].customFields = {
                ...docData.lineItems[index].customFields,
                [columnId]: fieldValues.value,
              };
            }
          });
        }
      });

      // Override totalQuantity for BOL to reflect net shipped after returns
      docData.totalQuantity = totalNetQty;

      // add individual PickList inventoryItems
      docData.lineItems.forEach((lineItem, index) => {
        const shipmentLineItem = bolData.shipmentLineItems.items[index];

        shipmentLineItem.inventoryItems?.items.forEach((itm) => {
          const netPickedQty = Math.max(
            0,
            Number(itm?.quantityToPick || 0) -
              Number(itm?.quantityReturned || 0),
          );
          const pickListItem = {
            ...lineItem,
            quantity: netPickedQty,
            location: itm.inventoryItem?.locationFullName,
            poNumber:
              itm.inventoryItem?.inventory?.poNumber || lineItem.poNumber,
            railCarNumber:
              itm.inventoryItem?.inventory?.railCarNumber ||
              lineItem.railCarNumber,
          };
          docData.pickList.push(pickListItem);
        });
      });

      // Finalize totalPCS
      if (!allMissingPCS) {
        if (somePCSNull) {
          docData.totalPCS = "N/A";
        } else {
          docData.totalPCS = totalPCS;
        }
      }

      // Finalize totalMBF
      if (!allMissingMBF) {
        if (someMBFNull) {
          docData.totalMBF = "N/A";
        } else {
          docData.totalMBF = totalMBF;
        }
      }

      // Finalize totalMSF
      if (!allMissingMSF) {
        if (someMSFNull) {
          docData.totalMSF = "N/A";
        } else {
          docData.totalMSF = totalMSF;
        }
      }
    }

    return docData;
  };

  const returnCandidates = useMemo(() => {
    const lineItems = data?.shipmentLineItems?.items || [];

    const rows = [];
    lineItems.forEach((li) => {
      const lineQty = Number(li?.totalQuantityToPick || 0);
      const units = li?.inventory?.inventoryUOM || data?.totalUOM || "";
      const productName =
        li?.inventory?.productName || li?.inventory?.name || "";
      const lotNumber = li?.lotNumber || li?.inventory?.lotNumber || "";

      const invItems = li?.inventoryItems?.items || [];
      if (invItems.length > 0) {
        invItems.forEach((invItem) => {
          rows.push({
            key: invItem.id,
            shipmentLineItemId: li.id,
            shipmentLineItemInventoryItemId: invItem.id,
            inventoryId: li?.inventory?.id,
            inventory: li?.inventory,
            inventoryItemId: invItem?.inventoryItemId,
            inventoryItem: invItem?.inventoryItem,
            productName,
            lotNumber,
            units: invItem?.units || li?.units || units,
            pickedQty: Number(invItem?.quantityToPick || 0),
            alreadyReturnedQty: Number(invItem?.quantityReturned || 0),
          });
        });
      } else {
        // Fallback for bulk / non-itemized picks
        rows.push({
          key: li.id,
          shipmentLineItemId: li.id,
          shipmentLineItemInventoryItemId: null,
          inventoryId: li?.inventory?.id,
          inventory: li?.inventory,
          inventoryItemId: null,
          inventoryItem: null,
          productName,
          lotNumber,
          units: li?.units || units,
          pickedQty: lineQty,
          alreadyReturnedQty: Number(li?.totalQuantityReturned || 0),
        });
      }
    });

    return rows.filter((row) => {
      const pickedQty = Number(row.pickedQty || 0);
      const alreadyReturnedQty = Number(row.alreadyReturnedQty || 0);
      const remainingReturnableQty = Math.max(
        0,
        pickedQty - alreadyReturnedQty,
      );

      return remainingReturnableQty > 0 && row.inventory;
    });
  }, [data]);

  const openReturnDialog = () => {
    setReturnItems(
      returnCandidates.map((c) => ({
        shipmentLineItemId: c.shipmentLineItemId,
        shipmentLineItemInventoryItemId: c.shipmentLineItemInventoryItemId,
        pickedQty: c.pickedQty,
        returnQty: 0,
      })),
    );
    setReturnDialogOpen(true);
  };

  const closeReturnDialog = () => setReturnDialogOpen(false);

  const setReturnQty = (
    shipmentLineItemId,
    shipmentLineItemInventoryItemId,
    value,
  ) => {
    setReturnItems((prev) =>
      prev.map((itm) =>
        itm.shipmentLineItemId !== shipmentLineItemId ||
        itm.shipmentLineItemInventoryItemId !== shipmentLineItemInventoryItemId
          ? itm
          : { ...itm, returnQty: value },
      ),
    );
  };

  const setReturnAll = () => {
    setReturnItems((prev) =>
      prev.map((itm) => {
        const matchingRow = returnCandidates.find(
          (row) =>
            row.shipmentLineItemId === itm.shipmentLineItemId &&
            row.shipmentLineItemInventoryItemId ===
              itm.shipmentLineItemInventoryItemId,
        );
        const remainingReturnableQty = Math.max(
          0,
          Number(matchingRow?.pickedQty || 0) -
            Number(matchingRow?.alreadyReturnedQty || 0),
        );

        return { ...itm, returnQty: remainingReturnableQty };
      }),
    );
  };

  const totalReturnQty = useMemo(() => {
    return returnItems.reduce(
      (sum, itm) => sum + Number(itm?.returnQty || 0),
      0,
    );
  }, [returnItems]);

  const getReturnReasonLabel = (value) => {
    const found = ReturnReasonChoices.find((c) => c.id === value);
    return found?.name || value || "Other";
  };

  const validateReturnQuantities = () => {
    // Per-row: return <= picked; Per-line-item: sum(return) <= shipped (totalQuantityToPick)
    const rowKeyToPickedQty = new Map(
      returnCandidates.map((row) => [
        `${row.shipmentLineItemId}:${
          row.shipmentLineItemInventoryItemId || ""
        }`,
        Number(row.pickedQty || 0),
      ]),
    );
    const rowKeyToAlreadyReturnedQty = new Map(
      returnCandidates.map((row) => [
        `${row.shipmentLineItemId}:${
          row.shipmentLineItemInventoryItemId || ""
        }`,
        Number(row.alreadyReturnedQty || 0),
      ]),
    );

    const lineIdToShippedQty = new Map(
      (data?.shipmentLineItems?.items || []).map((li) => [
        li.id,
        Number(li?.totalQuantityToPick || 0),
      ]),
    );
    const lineIdToAlreadyReturnedQty = new Map(
      (data?.shipmentLineItems?.items || []).map((li) => {
        const fromLine = Number(li?.totalQuantityReturned || 0);
        const fromItems = (li?.inventoryItems?.items || []).reduce(
          (sum, itm) => sum + Number(itm?.quantityReturned || 0),
          0,
        );
        return [li.id, fromLine || fromItems || 0];
      }),
    );

    const lineIdToReturnSum = new Map();

    for (const itm of returnItems || []) {
      const rowKey = `${itm.shipmentLineItemId}:${
        itm.shipmentLineItemInventoryItemId || ""
      }`;
      const pickedQty = Number(rowKeyToPickedQty.get(rowKey) || 0);
      const alreadyReturnedQty = Number(
        rowKeyToAlreadyReturnedQty.get(rowKey) || 0,
      );
      const returnQty = Number(itm?.returnQty || 0);

      if (!Number.isFinite(returnQty) || returnQty < 0) {
        notify("Return qty cannot be negative.", "warning");
        return false;
      }
      const remainingRow = Math.max(0, pickedQty - alreadyReturnedQty);
      if (returnQty > remainingRow) {
        notify(
          "Return qty cannot exceed the remaining shipped qty for that picked item.",
          "warning",
        );
        return false;
      }

      const prev = Number(lineIdToReturnSum.get(itm.shipmentLineItemId) || 0);
      lineIdToReturnSum.set(itm.shipmentLineItemId, prev + returnQty);
    }

    for (const [lineId, returnSum] of lineIdToReturnSum.entries()) {
      const shippedQty = Number(lineIdToShippedQty.get(lineId) || 0);
      const alreadyReturnedLine = Number(
        lineIdToAlreadyReturnedQty.get(lineId) || 0,
      );
      const remainingLine = Math.max(0, shippedQty - alreadyReturnedLine);
      if (returnSum > remainingLine) {
        notify(
          "Total returned for a line item cannot exceed the quantity originally shipped.",
          "warning",
        );
        return false;
      }
    }

    return true;
  };

  const generateBOL = async () => {
    setIsSaving(true);
    debugger;

    try {
      let bolNumber = data.bolNumber;

      if (!bolNumber) {
        let counter;
        if (data.pickupNumber && operatingAccount?.generatePickupNumber) {
          counter = extractCounter(data.pickupNumber);
          if (!counter) counter = await incrementBOLCounter();
        } else {
          counter = await incrementBOLCounter();
        }
        bolNumber = `BOL-${counter}`;
        setBolNumber && setBolNumber(bolNumber);
      }

      const gateCheckIn = await fetchAtsGateCheckIn(data); // TEMPORARY — ATS only
      const docData = getBOLData(data, bolNumber, gateCheckIn);
      docData.carrierSignature = data.carrierSignatureKey
        ? {
            _type: "image",
            source: data.carrierSignatureKey,
            format: "image/png",
            width: 100,
            height: 50,
          }
        : undefined;
      docData.logo = window.appUser.reloadYard.logoImage
        ? {
            _type: "image",
            source: window.appUser.reloadYard.logoImage.key,
            format: "image/png",
            width: 70,
            height: 70,
          }
        : undefined;
      docData.transloadsLogo = {
        _type: "image",
        source: `${FilePaths.TEMPLATES}/${FilePaths.TRANSLOADS_LOGO}`,
        format: "image/png",
        width: 100,
        height: 25,
      };
      docData.yardSignature = data.yardSignatureKey
        ? {
            _type: "image",
            source: data.yardSignatureKey,
            format: "image/png",
            width: 100,
            height: 50,
          }
        : undefined;

      const outputPath = `${data.reloadYardId}/${FilePaths.OUTBOUND}/${
        data.id
      }/${bolNumber}_${new Date()
        .toLocaleDateString()
        .replaceAll("/", "_")}.docx`;

      const createDocx = async (templateKey) =>
        API.post("transREST", "/createDocx", {
          body: {
            templateKey,
            data: docData,
            outputFileKey: outputPath,
          },
        });

      const initialTemplatePath = getTemplatePath(
        operatingAccount || data.account,
        "bolTemplate",
      );
      let response = await createDocx(initialTemplatePath);

      // If the cached key is stale (template replaced), re-fetch the latest template key and retry once.
      if (!response?.body?.key) {
        const freshTemplatePath = await fetchTemplatePath(
          operatingAccount || data.account,
          "bolTemplate",
        );
        response = await createDocx(freshTemplatePath);
      }

      if (response.body?.key) {
        const file = await Storage.get(response.body.key, { download: true });
        downloadFile(file, response.body.key);
        setBolFile && setBolFile(response.body);

        try {
          const outboundToUpdate = {
            ...data,
            bolFile: response.body,
            bolNumber: bolNumber,
            invoiceId: data.invoiceId || " ",
          };
          // must call lambda to avoid resetting updatedAt timestamp
          await updateLambdaOutboundMini(outboundToUpdate);
        } catch (error) {
          notify("BOL created with errors.", "warning");
          logError(
            "OutboundEdit:OutboundEditActions:generateBOL:updateOutbound",
            error,
            false,
          );
          setIsSaving(false);
          return;
        }

        if (response.success) notify("BOL created successfully.", "success");
        else if (response.warnings) {
          notify("BOL created with errors.", "warning");
          logError(
            "OutboundEdit:OutboundEditActions:generateBOL:warning",
            response.warnings,
            false,
            null,
          );
        }
      } else {
        logError(
          "OutboundEdit:OutboundEditActions:generateBOL:error",
          response.error,
          notify,
          null,
        );
        setIsSaving(false);
        return;
      }
    } catch (error) {
      logError(
        "OutboundEdit:OutboundEditActions:generateBOL",
        error,
        notify,
        null,
      );
    } finally {
      setIsSaving(false);
      !setBolFile && refresh(); // refresh read-only view
    }
  };

  const generatePickTicket = async () => {
    setIsSaving(true);
    debugger;

    try {
      const docData = getBOLData(data, "");

      const templatePath = `${window.appUser.reloadYardId}/docxTemplates/PickTicket_Template_STL_v1.docx`;
      const outputPath = `${data.reloadYardId}/${FilePaths.OUTBOUND}/${
        data.id
      }/PICK_TICKET_${new Date()
        .toLocaleDateString()
        .replaceAll("/", "_")}.docx`;

      const response = await API.post("transREST", "/createDocx", {
        body: {
          templateKey: templatePath,
          data: docData,
          outputFileKey: outputPath,
        },
      });

      if (response.body?.key) {
        const file = await Storage.get(response.body.key, { download: true });
        downloadFile(file, response.body.key);

        if (response.success) notify("PickTicket created successfully.");
        else if (response.warnings) {
          notify("PickTicket created with errors.", "warning");
          logError(
            "OutboundEdit:OutboundEditActions:generatePickTicket:warning",
            response.warnings,
            false,
            null,
          );
        }
      } else {
        logError(
          "OutboundEdit:OutboundEditActions:generatePickTicket:error",
          response.error,
          notify,
          null,
        );
        setIsSaving(false);
        return;
      }
    } catch (error) {
      logError(
        "OutboundEdit:OutboundEditActions:generatePickTicket",
        error,
        notify,
        null,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleReturn = async () => {
    setIsSaving(true);
    setReturnDialogOpen(false);
    debugger;

    try {
      if (!validateReturnQuantities()) {
        setIsSaving(false);
        setReturnDialogOpen(true);
        return;
      }

      const updateDateTime = new Date().toISOString();

      // Build the return inbound header.
      // If the outbound has a BOL file, attach it to the new inbound so the
      // return keeps a copy of the shipment document.
      const lotTypes = new Set();
      const yardCommodities = new Set();
      let areaId = null;
      let zoneId = null;
      let totalReturnedQty = 0;

      const returnQtyByRowKey = new Map(
        (returnItems || []).map((itm) => [
          `${itm.shipmentLineItemId}:${
            itm.shipmentLineItemInventoryItemId || ""
          }`,
          Number(itm?.returnQty || 0),
        ]),
      );

      // Return delta totals per ShipmentLineItem (used for updating outbound + recalculating totals)
      const lineReturnDelta = new Map();
      returnCandidates.forEach((row) => {
        const rowKey = `${row.shipmentLineItemId}:${
          row.shipmentLineItemInventoryItemId || ""
        }`;
        const deltaReturned = Number(returnQtyByRowKey.get(rowKey) || 0);
        if (deltaReturned <= 0) return;
        const prev = Number(lineReturnDelta.get(row.shipmentLineItemId) || 0);
        lineReturnDelta.set(row.shipmentLineItemId, prev + deltaReturned);
      });

      // Summarize totals + derive inbound header fields
      returnCandidates.forEach((row) => {
        const rowKey = `${row.shipmentLineItemId}:${
          row.shipmentLineItemInventoryItemId || ""
        }`;
        const returnQty = Number(returnQtyByRowKey.get(rowKey) || 0);
        if (returnQty <= 0) return;

        totalReturnedQty += returnQty;
        const inv = row.inventory;
        if (inv) {
          if (inv.lotType) lotTypes.add(inv.lotType);
          if (inv.reloadYardCommodityId)
            yardCommodities.add(inv.reloadYardCommodityId);
          areaId = inv.areaId || areaId;
          zoneId = inv.zoneId || zoneId;
        }
      });

      if (!totalReturnedQty || totalReturnedQty <= 0) {
        notify("Enter at least one return quantity greater than 0.", "warning");
        setIsSaving(false);
        return;
      }

      const historyNote = {
        type: NoteType.SHIPMENT,
        date: updateDateTime,
        by: window.appUser?.fullName,
        text: `Return Created. Qty: ${totalReturnedQty} ${
          data?.totalUOM || ""
        }.`,
      };

      const inboundInput = {
        id: uuidv4(),
        active: "true",
        groups: data.groups,
        status: InboundStatus.IN_TRANSIT,
        state: InboundState.NEW,
        accountName: data.accountName,
        accountId: data.accountId,
        billToAccountId: data.billToAccountId || data.accountId,
        shipFrom: data?.shipTo?.name || data.accountName,
        poNumber: data.poNumber,
        normalizedPoNumber: normalizeValue(data.poNumber),
        lotType: lotTypes.size === 1 ? [...lotTypes][0] : LotType.CARLOT,
        // Keep the return inbound header lotNumber generic.
        // Each cloned return inventory keeps the original source lotNumber.
        lotNumber: "RETURN",
        reloadYardCommodityId:
          yardCommodities.size > 0
            ? [...yardCommodities][0]
            : data.reloadYardCommodityId,
        carrierType: data?.carrierType || CarrierType.TRUCK,
        carrierName: data?.carrierName || undefined,
        railCarNumber: data?.railCarNumber || "",
        createDateTime: updateDateTime,
        scheduledDateTime: null,
        notifyDateTime: null,
        placedDateTime: null,
        releaseDateTime: null,
        totalUOM: data.totalUOM,
        reloadYardId: data.reloadYardId,
        areaId: areaId,
        zoneId: zoneId,
        lotNumberOverride: true,
        secondaryReview: false,
        skipInvoice: true,
        stockImport: false,
        returnShipment: true,
        containerDrop: false,
        sit: false,
        returnOutboundShipmentId: data?.id,
        returnReason: returnReason || ReturnReason.OTHER,
        history: [historyNote],
        instructions: `Return from Outbound ${data?.id || ""}${
          data?.bolNumber ? ` (BOL ${data.bolNumber})` : ""
        }. Reason: ${getReturnReasonLabel(returnReason)}.`,
        attachments: data.bolFile ? [data.bolFile] : undefined,
        createdAt: updateDateTime,
        updatedAt: updateDateTime,
      };
      const inboundCreateInput = sanitizeInboundLambda(inboundInput);

      // Build inbound inventories (tally sheet) from returned pick rows only
      const inventoryCreateList = [];
      let sortKeyCounter = SORTKEY_BUFFER;
      returnCandidates.forEach((row) => {
        const rowKey = `${row.shipmentLineItemId}:${
          row.shipmentLineItemInventoryItemId || ""
        }`;
        const qty = Number(returnQtyByRowKey.get(rowKey) || 0);
        const inv = row?.inventory;
        if (!inv || qty <= 0) return;

        const sourceLocation = row?.inventoryItem?.locationFullName || "";
        const barcode = row?.inventoryItem?.barcode || "";

        const historyNote = {
          type: NoteType.INVENTORY,
          date: updateDateTime,
          by: window.appUser?.fullName,
          text: `Return Created of ${qty} ${
            inv.inventoryUOM
          }. Reason: ${getReturnReasonLabel(returnReason)}.${
            sourceLocation ? ` Source: ${sourceLocation}.` : ""
          }${barcode ? ` Barcode: ${barcode}.` : ""}`,
        };

        const inventoryInput = {
          ...inv,
          id: uuidv4(),
          active: "true",
          groups: data.groups,
          accountName: data.accountName,
          accountId: data.accountId,
          reloadYardId: data.reloadYardId,
          inboundShipmentId: inboundCreateInput.id,
          quantityAdvised: qty,
          quantityReceived: 0,
          quantityOnHand: 0,
          quantityPhysical: 0,
          quantityCommitted: 0,
          receivedAt: updateDateTime,
          aisleId: null,
          locationId: null,
          depletedAt: null,
          lastOutboundId: null,
          turnDays: null,
          status: InventoryStatus.IN_TRANSIT,
          history: [historyNote],
          sortKey: sortKeyCounter++,
          returnShipmentLineItemId: row.shipmentLineItemId,
          createdAt: updateDateTime,
          updatedAt: updateDateTime,
        };
        const sanitizedInventoryInput = sanitizeInventoryLambda(inventoryInput);
        inventoryCreateList.push(sanitizedInventoryInput);
      });

      const resp = await API.post("transREST", "/returnCreate", {
        body: {
          inboundCreateInput: inboundCreateInput,
          inventoryCreateList: inventoryCreateList,
          outboundCreateInput: null,
          user: window.appUser.email,
          reloadYardId: window.appUser.reloadYardId,
        },
      });
      if (resp?.error) throw resp.error;

      // Persist returned quantities on the outbound (so totals/BOL can compute net shipped)
      try {
        // Update ShipmentLineItemInventoryItem.quantityReturned (incremental)
        const sliiUpdates = returnCandidates
          .filter((row) => row.shipmentLineItemInventoryItemId)
          .map((row) => {
            const rowKey = `${row.shipmentLineItemId}:${
              row.shipmentLineItemInventoryItemId || ""
            }`;
            const deltaReturned = Number(returnQtyByRowKey.get(rowKey) || 0);
            if (deltaReturned <= 0) return null;
            const newReturned =
              Number(row.alreadyReturnedQty || 0) + deltaReturned;
            return API.graphql(
              graphqlOperation(updateShipmentLineItemInventoryItem, {
                input: {
                  id: row.shipmentLineItemInventoryItemId,
                  quantityReturned: newReturned,
                },
              }),
            );
          })
          .filter(Boolean);

        // Update ShipmentLineItem.totalQuantityReturned (incremental)
        const lineUpdates = (data?.shipmentLineItems?.items || [])
          .map((li) => {
            const delta = Number(lineReturnDelta.get(li.id) || 0);
            if (delta <= 0) return null;
            const alreadyReturnedLine = Number(li?.totalQuantityReturned || 0);
            const newTotalReturned = alreadyReturnedLine + delta;
            return API.graphql(
              graphqlOperation(updateShipmentLineItem, {
                input: { id: li.id, totalQuantityReturned: newTotalReturned },
              }),
            );
          })
          .filter(Boolean);

        await Promise.all([...sliiUpdates, ...lineUpdates]);
      } catch (error) {
        // Return was created successfully; treat outbound return-field updates as non-blocking
        logError(
          "OutboundEdit:OutboundEditActions:handleReturn:updateReturnedQuantities",
          error,
          false,
        );
      }

      // Update outbound return summary fields and clear the direct BOL pointer.
      // The actual BOL copy for the return was attached above on inboundCreateInput.
      try {
        // Recalculate outbound totals after return so the Tally Sheet "Total Load"
        // reflects net shipped quantity.
        const nextLineReturnedById = new Map();
        (data?.shipmentLineItems?.items || []).forEach((li) => {
          const delta = Number(lineReturnDelta.get(li.id) || 0);
          const alreadyReturnedLine = Number(li?.totalQuantityReturned || 0);
          nextLineReturnedById.set(li.id, alreadyReturnedLine + delta);
        });
        const nextOutbound = {
          ...data,
          // outbound-level return fields (schema.graphql)
          returnShipment: true,
          returnReason: returnReason || ReturnReason.OTHER,
        };

        // Clear the outbound's direct BOL reference after the return is created.
        // The BOL file itself remains available on the return inbound attachment.
        if (data?.bolFile) {
          nextOutbound.bolFile = null;
          nextOutbound.bolNumber = null;
        }

        await updateLambdaOutboundMini(nextOutbound);
      } catch (error) {
        // Return was created successfully; treat BOL move as non-blocking
        logError(
          "OutboundEdit:OutboundEditActions:handleReturn:moveBol",
          error,
          false,
        );
      }

      notify("Return processed successfully.", "success");
      deepRedirect(
        props.history,
        `/inboundShipments/${inboundCreateInput.id}`,
        backPath,
      );
    } catch (error) {
      logError(
        "OutboundEdit:OutboundEditActions:handleReturn",
        error,
        notify,
        null,
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <TopToolbar>
      <Backdrop className={classes.backdrop} open={isSaving}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <HelpButton>
        <OutboundEditHelp {...props} bolData={getBOLData(data, "BOL-number")} />
      </HelpButton>
      {isMedium &&
        isAuthorized(AclType.INVOICES) &&
        data.status !== OutboundStatus.ADVICE_PENDING && (
          <CreateInvoiceButton
            {...props}
            record={data}
            backPath={backPath}
            disabled={loading}
          />
        )}
      {(getEnv() === "dev" ||
        window.appUser.reloadYardId ===
          "a52a7658-0e2f-49b9-9f41-e3ebff901ea4") && ( // STL Reload Yard ID
        <Button
          label="PICK TICKET"
          color="primary"
          disabled={loading || isSaving}
          onClick={generatePickTicket}
        >
          <DocumentIcon />
        </Button>
      )}
      {data.status !== OutboundStatus.ADVICE_PENDING && (
        <Button
          label="Create BOL"
          color="primary"
          disabled={loading || isSaving}
          onClick={generateBOL}
        >
          <DocumentIcon />
        </Button>
      )}
      {data && !data.status?.startsWith(OutboundStatus.ADVICE_ALL) && (
        <SendPOSDialogButton
          label="SEND"
          title="Send POS"
          record={data}
          operatingAccount={operatingAccount}
          email={operatingAccount?.posEmail || data.account?.posEmail}
          subjectDefault={getPOSEmailSubject(data, operatingAccount)}
          enclosePhotosDefault={
            operatingAccount?.posPhotos || data.account?.posPhotos
          }
          encloseDocsDefault={
            operatingAccount?.posAttachments || data.account?.posAttachments
          }
          handleSend={handleSendPOS}
          disabled={loading}
        />
      )}
      {data?.status === OutboundStatus.RELEASED && (
        <Button
          label="Return"
          color="primary"
          disabled={loading || isSaving}
          onClick={openReturnDialog}
        >
          <ReturnIcon />
        </Button>
      )}
      <Dialog
        open={returnDialogOpen}
        onClose={closeReturnDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Return Details</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>
            Create an inbound “Return” shipment for the quantities you enter
            below. You’ll receive/putaway the returned inventory to the desired
            location(s) using the normal inbound workflow.
          </Typography>

          <Grid container spacing={1}>
            <Grid item xs={12} md={7}>
              <TextField
                label="Reason"
                fullWidth
                select
                margin="dense"
                variant="outlined"
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                SelectProps={{ native: true }}
              >
                {ReturnReasonChoices.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={5}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "flex-end",
                  height: "100%",
                }}
              >
                <Button
                  label="Return All"
                  variant="outlined"
                  onClick={setReturnAll}
                  disabled={isSaving}
                />
              </div>
            </Grid>
          </Grid>

          <Grid container spacing={1} style={{ marginTop: 8 }}>
            {returnCandidates.map((c) => {
              const stateRow =
                returnItems.find(
                  (r) =>
                    r.shipmentLineItemId === c.shipmentLineItemId &&
                    r.shipmentLineItemInventoryItemId ===
                      c.shipmentLineItemInventoryItemId,
                ) || {};
              const pickedQty = Number(c.pickedQty || 0);
              const returnQty = Number(stateRow?.returnQty || 0);
              const locationFullName = c?.inventoryItem?.locationFullName || "";
              const barcode = c?.inventoryItem?.barcode || "";

              return (
                <Grid item xs={12} key={c.key}>
                  <Grid container spacing={1} alignItems="flex-start">
                    <Grid item xs={12} md={7}>
                      <Typography variant="subtitle2">
                        {c.productName} {c.lotNumber ? `(${c.lotNumber})` : ""}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Picked: {pickedQty} {getUnitName(c.units) || c.units}
                        {locationFullName ? ` • ${locationFullName}` : ""}
                        {barcode ? ` • ${barcode}` : ""}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      {c.alreadyReturnedQty > 0 && (
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          style={{ display: "block" }}
                        >
                          Returned so far:{" "}
                          {trimDecimal(c.alreadyReturnedQty, 3)}{" "}
                          {getUnitName(c.units) || c.units}
                        </Typography>
                      )}
                      <TextField
                        label="Return Qty"
                        type="number"
                        fullWidth
                        margin="dense"
                        variant="outlined"
                        value={Number.isFinite(returnQty) ? returnQty : 0}
                        inputProps={{
                          min: 0,
                          max: Math.max(
                            0,
                            pickedQty - Number(c.alreadyReturnedQty || 0),
                          ),
                          step: "any",
                        }}
                        onChange={(e) => {
                          const raw =
                            e.target.value === "" ? 0 : Number(e.target.value);
                          const max = Math.max(
                            0,
                            pickedQty - Number(c.alreadyReturnedQty || 0),
                          );
                          const next = Number.isFinite(raw)
                            ? Math.min(Math.max(raw, 0), max)
                            : 0;
                          setReturnQty(
                            c.shipmentLineItemId,
                            c.shipmentLineItemInventoryItemId,
                            next,
                          );
                        }}
                        error={
                          returnQty >
                          Math.max(
                            0,
                            pickedQty - Number(c.alreadyReturnedQty || 0),
                          )
                        }
                        helperText={false}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            label="Cancel"
            color="default"
            onClick={closeReturnDialog}
            disabled={isSaving}
          />
          <Typography
            variant="body2"
            style={{ marginLeft: "auto", marginRight: 16 }}
          >
            Total Return: {trimDecimal(totalReturnQty, 3)}{" "}
            {getUnitName(data?.totalUOM) || ""}
          </Typography>
          <Button
            label="Create Return"
            color="primary"
            variant="contained"
            onClick={handleReturn}
            disabled={isSaving}
          />
        </DialogActions>
      </Dialog>
      <UnlinkButton record={data} disabled={loading || isSaving} />
      <BackButton {...props} backPath={backPath} />
    </TopToolbar>
  );
};
